/**
 * MyDashboardsTab — list+tiles of user dashboards. iter-121.39 cosmetic
 * redesign: modernised cards with tinted icon plates, softer rounded corners,
 * cleaner section headers, and refined table rows. All data-testids,
 * handlers, and API wiring are preserved verbatim.
 */
import React, { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import {
    Database, Sparkles, LayoutDashboard, Trash2, Layers, ChevronDown, Plus, Loader2,
    BarChart3, Megaphone, Wallet, Users as UsersIcon, ShoppingCart,
} from "lucide-react";
import EmptyStateCTAs from "@/components/EmptyStateCTAs";

/* -------------------------------------------------------------------------- */
/*  Icon plate — tinted background that hints at the dashboard's domain. When */
/*  the name matches a well-known platform (Google, Meta, Facebook, …) we     */
/*  render the official brand glyph via simpleicons.org CDN; otherwise we     */
/*  fall back to a tinted Lucide icon. Mirrors ConnectorIcon's pattern so we  */
/*  stay visually consistent with /data and /connectors pages.                */
/* -------------------------------------------------------------------------- */
const BRAND_GLYPHS = [
    { match: /(meta\s*ads|facebook|instagram)/i,  src: "https://cdn.simpleicons.org/meta/0866FF",        tone: "from-[#0866FF]/15 to-[#0866FF]/5  border-[#0866FF]/25" },
    { match: /google\s*ads/i,                      src: "https://cdn.simpleicons.org/googleads/4285F4",   tone: "from-[#4285F4]/15 to-[#4285F4]/5  border-[#4285F4]/25" },
    { match: /\b(ga4|google\s*analytics)\b/i,      src: "https://cdn.simpleicons.org/googleanalytics/F9AB00", tone: "from-[#F9AB00]/15 to-[#F9AB00]/5 border-[#F9AB00]/25" },
    { match: /\bgoogle\b/i,                        src: "https://cdn.simpleicons.org/google/4285F4",      tone: "from-[#4285F4]/15 to-[#4285F4]/5  border-[#4285F4]/25" },
    { match: /\blinkedin\b/i,                      src: "https://cdn.simpleicons.org/linkedin/0A66C2",    tone: "from-[#0A66C2]/15 to-[#0A66C2]/5  border-[#0A66C2]/25" },
    { match: /\b(twitter|tweet|\bx\b)\b/i,         src: "https://cdn.simpleicons.org/x/000000",          tone: "from-foreground/10 to-foreground/5 border-foreground/15" },
    { match: /\btiktok\b/i,                        src: "https://cdn.simpleicons.org/tiktok/000000",      tone: "from-foreground/10 to-foreground/5 border-foreground/15" },
    { match: /\bshopify\b/i,                       src: "https://cdn.simpleicons.org/shopify/96BF48",     tone: "from-[#96BF48]/15 to-[#96BF48]/5  border-[#96BF48]/25" },
    { match: /\b(amazon|aws|s3)\b/i,               src: "https://cdn.simpleicons.org/amazonaws/FF9900",   tone: "from-[#FF9900]/15 to-[#FF9900]/5  border-[#FF9900]/25" },
    { match: /\bstripe\b/i,                        src: "https://cdn.simpleicons.org/stripe/635BFF",      tone: "from-[#635BFF]/15 to-[#635BFF]/5  border-[#635BFF]/25" },
    { match: /\b(postgres|postgresql)\b/i,         src: "https://cdn.simpleicons.org/postgresql/4169E1",  tone: "from-[#4169E1]/15 to-[#4169E1]/5  border-[#4169E1]/25" },
    { match: /\bmysql\b/i,                         src: "https://cdn.simpleicons.org/mysql/4479A1",       tone: "from-[#4479A1]/15 to-[#4479A1]/5  border-[#4479A1]/25" },
    { match: /\b(sql\s*server|mssql|azure\s*sql)\b/i, src: "https://cdn.simpleicons.org/microsoftsqlserver/CC2927", tone: "from-[#CC2927]/15 to-[#CC2927]/5 border-[#CC2927]/25" },
    { match: /\bazure\b/i,                         src: "https://cdn.simpleicons.org/microsoftazure/0078D4", tone: "from-[#0078D4]/15 to-[#0078D4]/5 border-[#0078D4]/25" },
    { match: /\b(salesforce|sfdc)\b/i,             src: "https://cdn.simpleicons.org/salesforce/00A1E0",  tone: "from-[#00A1E0]/15 to-[#00A1E0]/5  border-[#00A1E0]/25" },
    { match: /\bhubspot\b/i,                       src: "https://cdn.simpleicons.org/hubspot/FF7A59",     tone: "from-[#FF7A59]/15 to-[#FF7A59]/5  border-[#FF7A59]/25" },
    { match: /\bsnowflake\b/i,                     src: "https://cdn.simpleicons.org/snowflake/29B5E8",   tone: "from-[#29B5E8]/15 to-[#29B5E8]/5  border-[#29B5E8]/25" },
];

function pickIconConfig(name = "", category = "") {
    const haystack = `${name} ${category}`.toLowerCase();
    if (/(marketing|campaign|ads|ad\b)/.test(haystack))      return { Icon: Megaphone, tone: "from-primary/15 to-primary/5 text-primary border-primary/20" };
    if (/(sales|revenue|pipeline)/.test(haystack))           return { Icon: BarChart3, tone: "from-emerald-500/15 to-emerald-500/5 text-emerald-600 dark:text-emerald-400 border-emerald-500/20" };
    if (/(finance|cost|spend|budget|billing)/.test(haystack)) return { Icon: Wallet, tone: "from-amber-500/15 to-amber-500/5 text-amber-600 dark:text-amber-400 border-amber-500/20" };
    if (/(hr|people|team|employee)/.test(haystack))          return { Icon: UsersIcon, tone: "from-purple-500/15 to-purple-500/5 text-purple-600 dark:text-purple-400 border-purple-500/20" };
    if (/(commerce|order|product)/.test(haystack))           return { Icon: ShoppingCart, tone: "from-indigo-500/15 to-indigo-500/5 text-indigo-600 dark:text-indigo-400 border-indigo-500/20" };
    if (/(traffic|analytic|visit|session|user)/.test(haystack)) return { Icon: BarChart3, tone: "from-sky-500/15 to-sky-500/5 text-sky-600 dark:text-sky-400 border-sky-500/20" };
    return { Icon: LayoutDashboard, tone: "from-primary/15 to-primary/5 text-primary border-primary/20" };
}

function pickBrandGlyph(name = "", sourceLabel = "") {
    const haystack = `${name} ${sourceLabel}`;
    for (const b of BRAND_GLYPHS) {
        if (b.match.test(haystack)) return b;
    }
    return null;
}

function IconPlate({ name, category, sourceLabel, size = "md" }) {
    const [broken, setBroken] = useState(false);
    const brand = !broken ? pickBrandGlyph(name, sourceLabel) : null;
    const dims = size === "sm" ? "h-9 w-9" : "h-11 w-11";
    const iconDims = size === "sm" ? "h-4 w-4" : "h-5 w-5";
    const imgDims = size === "sm" ? "h-4 w-4" : "h-5 w-5";
    if (brand) {
        return (
            <div className={`${dims} rounded-xl border bg-gradient-to-br ${brand.tone} flex items-center justify-center shrink-0`}>
                <img src={brand.src} alt="" className={`${imgDims} object-contain`} loading="lazy" onError={() => setBroken(true)} />
            </div>
        );
    }
    const { Icon, tone } = pickIconConfig(name, category);
    return (
        <div className={`${dims} rounded-xl border bg-gradient-to-br ${tone} flex items-center justify-center shrink-0`}>
            <Icon className={iconDims} strokeWidth={1.6} />
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  Collapsible section with persistent open/closed preference.               */
/* -------------------------------------------------------------------------- */
function Section({ title, count, children, testId, storageKey }) {
    const lsKey = `analyzio.my-dashboards.${storageKey}.collapsed`;
    const [collapsed, setCollapsed] = useState(() => localStorage.getItem(lsKey) === "1");
    useEffect(() => { localStorage.setItem(lsKey, collapsed ? "1" : "0"); }, [lsKey, collapsed]);
    return (
        <div className="mb-8" data-testid={testId}>
            <button onClick={() => setCollapsed((x) => !x)}
                className="w-full flex items-center gap-2 mb-3.5 text-left group transition-opacity"
                data-testid={`${testId}-toggle`}>
                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${collapsed ? "-rotate-90" : ""}`} />
                <h3 className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">{title}</h3>
                <span className="ml-1 inline-flex items-center justify-center h-5 min-w-[1.5rem] px-1.5 rounded-full bg-secondary/60 text-[10px] font-mono text-muted-foreground border border-border">{count}</span>
            </button>
            {!collapsed && children}
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  Table view — modernised rows with tighter type ramp and hover affordances */
/* -------------------------------------------------------------------------- */
function MyDashboardsTable({ rows, ctx }) {
    const { canWriteData, canWriteDashboard, openDashboard, setTileSourceTarget, del } = ctx;
    return (
        <div className="border border-border rounded-xl bg-card overflow-x-auto shadow-sm" data-testid="my-dashboards-list">
            <table className="w-full text-sm min-w-[760px]">
                <thead className="bg-secondary/40">
                    <tr>
                        <th className="text-left px-4 py-2.5 eyebrow">Name</th>
                        <th className="text-left px-4 py-2.5 eyebrow w-24">Category</th>
                        <th className="text-left px-4 py-2.5 eyebrow w-20">Widgets</th>
                        <th className="text-left px-4 py-2.5 eyebrow">Source</th>
                        <th className="text-left px-4 py-2.5 eyebrow w-40">Created by</th>
                        <th className="w-10" />
                    </tr>
                </thead>
                <tbody>
                    {rows.map((d) => {
                        const isPrebuilt = d.source_gallery_template_kind === "prebuilt_dashboard";
                        return (
                            <tr key={d.id} className="border-t border-border hover:bg-secondary/30 cursor-pointer transition-colors" onClick={() => openDashboard(d)} data-testid={`my-dashboard-row-${d.id}`}>
                                <td className="px-4 py-2.5">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-medium truncate">{d.name}</span>
                                        {isPrebuilt && (
                                            <Badge variant="outline" className="font-mono text-[9px] border-primary/30 bg-primary/10 text-primary gap-0.5" data-testid={`my-dashboard-prebuilt-badge-${d.id}`}>
                                                <Sparkles className="h-2.5 w-2.5" /> PREBUILT
                                            </Badge>
                                        )}
                                        {canWriteData && d.needs_source && (
                                            <Badge
                                                variant="outline"
                                                className="font-mono text-[9px] border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300 gap-0.5 cursor-pointer hover:bg-amber-500/20 transition-colors"
                                                onClick={(e) => { e.stopPropagation(); setTileSourceTarget({ id: d.id, name: d.name }); }}
                                                title="Connect data — pick an existing source or create a new one"
                                                data-testid={`my-dashboard-connect-data-${d.id}`}
                                            >
                                                <Database className="h-2.5 w-2.5" /> Connect data
                                            </Badge>
                                        )}
                                        {canWriteData && !d.needs_source && Boolean((d.attached_sources?.length || 0) || (d.bind_to_models?.length || 0)) && (
                                            <Badge
                                                variant="outline"
                                                className="font-mono text-[9px] border-primary/30 bg-primary/10 text-primary gap-0.5 cursor-pointer hover:bg-primary/20 transition-colors"
                                                onClick={(e) => { e.stopPropagation(); setTileSourceTarget({ id: d.id, name: d.name }); }}
                                                title="Add another data source — pick an existing one or create new"
                                                data-testid={`my-dashboard-add-source-${d.id}`}
                                            >
                                                <Plus className="h-2.5 w-2.5" /> Add source
                                            </Badge>
                                        )}
                                        {d.is_unified && (
                                            <Badge variant="outline" className="font-mono text-[9px] border-primary/30 bg-primary/10 text-primary gap-0.5" data-testid={`my-dashboard-unified-${d.id}`}>
                                                <Layers className="h-2.5 w-2.5" /> UNIFIED
                                            </Badge>
                                        )}
                                    </div>
                                    {d.description && <div className="text-[11px] text-muted-foreground truncate mt-0.5">{d.description}</div>}
                                </td>
                                <td className="px-4 py-2.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{d.category || "—"}</td>
                                <td className="px-4 py-2.5 font-mono text-[11px]">{d.widgets?.length || 0}</td>
                                <td className="px-4 py-2.5 text-xs text-muted-foreground truncate" title={d.source_label || ""}>{d.source_label || "—"}</td>
                                <td className="px-4 py-2.5 text-xs text-muted-foreground truncate" title={d.created_by_email || ""}>{d.created_by_name || "—"}</td>
                                <td className="px-4 py-2.5 text-right">
                                    {(d.can_delete !== undefined ? d.can_delete : canWriteDashboard) && (
                                        <button onClick={(e) => { e.stopPropagation(); del(d.id, d.name); }} className="text-muted-foreground hover:text-destructive transition-colors" data-testid={`my-dashboard-delete-${d.id}`}>
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  Tile view — premium card design with icon plate, badge row, and clean    */
/*  footer. Hover state lifts the card subtly and reveals the delete action. */
/* -------------------------------------------------------------------------- */
function MyDashboardsTiles({ rows, ctx }) {
    const { canWriteData, canWriteDashboard, openDashboard, setTileSourceTarget, del } = ctx;
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {rows.map((d) => {
                const isPrebuilt = d.source_gallery_template_kind === "prebuilt_dashboard";
                return (
                    <div key={d.id}
                         className="group relative border border-border bg-card rounded-xl p-5 cursor-pointer shadow-sm hover:shadow-md hover:border-foreground/15 hover:-translate-y-0.5 transition-all duration-200"
                         onClick={() => openDashboard(d)}
                         data-testid={`my-dashboard-tile-${d.id}`}>
                        {/* Top row: icon + delete */}
                        <div className="flex items-start justify-between mb-3.5">
                            <IconPlate name={d.name} category={d.category} sourceLabel={d.source_label} />
                             {(d.can_delete !== undefined ? d.can_delete : canWriteDashboard) && (
                                <button onClick={(e) => { e.stopPropagation(); del(d.id, d.name); }}
                                        className="opacity-0 group-hover:opacity-100 h-8 w-8 rounded-md flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all"
                                        title="Delete dashboard"
                                        data-testid={`my-dashboard-delete-${d.id}`}>
                                    <Trash2 className="h-3.5 w-3.5" />
                                </button>
                            )}
                        </div>

                        {/* Title + description */}
                        <div className="mb-3">
                            <div className="text-[15px] font-semibold leading-snug text-foreground line-clamp-2">{d.name}</div>
                            {d.description && (
                                <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{d.description}</div>
                            )}
                        </div>

                        {/* Badge row */}
                        <div className="flex flex-wrap items-center gap-1.5 mb-3">
                            {d.category && !(isPrebuilt && d.category.toLowerCase() === "prebuilt") && (
                                <Badge variant="outline" className="font-mono text-[10px] bg-secondary/50 border-border">{d.category}</Badge>
                            )}
                            {isPrebuilt && (
                                <Badge variant="outline" className="font-mono text-[10px] border-primary/30 bg-primary/10 text-primary gap-1" data-testid={`my-dashboard-prebuilt-badge-${d.id}`}>
                                    <Sparkles className="h-3 w-3" /> PREBUILT
                                </Badge>
                            )}
                            {d.created_via === "sense_ai_builder" && !d.source_gallery_template_id && (
                                <Badge variant="outline"
                                       className="font-mono text-[10px] border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400 gap-1"
                                       title={d.builder_prompt
                                           ? `Generated from prompt: "${d.builder_prompt}"`
                                           : "Generated by the Sense AI Builder"}
                                       data-testid={`my-dashboard-builder-badge-${d.id}`}>
                                    <Sparkles className="h-3 w-3" /> SENSE AI
                                </Badge>
                            )}
                            {d.is_unified && (
                                <Badge variant="outline" className="font-mono text-[10px] border-primary/30 bg-primary/10 text-primary gap-1" data-testid={`my-dashboard-unified-${d.id}`}>
                                    <Layers className="h-3 w-3" /> UNIFIED
                                    {d.source_count > 1 && (
                                        <span className="ml-1 opacity-80">· {d.source_count} sources</span>
                                    )}
                                </Badge>
                            )}
                            {(d.tags || []).includes("personal") && (
                                <Badge variant="outline" className="font-mono text-[10px] bg-secondary/50 border-border">PERSONAL</Badge>
                            )}
                            {canWriteData && d.needs_source && (
                                <Badge
                                    variant="outline"
                                    className="font-mono text-[10px] border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300 gap-1 cursor-pointer hover:bg-amber-500/20 transition-colors"
                                    onClick={(e) => { e.stopPropagation(); setTileSourceTarget({ id: d.id, name: d.name }); }}
                                    title="Connect data — pick an existing source or create a new one"
                                    data-testid={`my-dashboard-tile-connect-data-${d.id}`}
                                >
                                    <Database className="h-3 w-3" /> Connect data
                                </Badge>
                            )}
                            {canWriteData && !d.needs_source && Boolean((d.attached_sources?.length || 0) || (d.bind_to_models?.length || 0)) && (
                                <Badge
                                    variant="outline"
                                    className="font-mono text-[10px] border-primary/30 bg-primary/10 text-primary gap-1 cursor-pointer hover:bg-primary/20 transition-colors"
                                    onClick={(e) => { e.stopPropagation(); setTileSourceTarget({ id: d.id, name: d.name }); }}
                                    title="Add another data source — pick an existing one or create new"
                                    data-testid={`my-dashboard-tile-add-source-${d.id}`}
                                >
                                    <Plus className="h-3 w-3" /> Add source
                                </Badge>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="pt-3 border-t border-border/60 flex items-center justify-between gap-2">
                            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                                {d.widgets?.length || 0} widgets
                            </span>
                            <span className="font-mono text-[10px] text-muted-foreground flex items-center gap-1 truncate min-w-0" title={d.source_label || ""}>
                                <Database className="h-3 w-3 shrink-0" /> <span className="truncate">{d.source_label || "—"}</span>
                            </span>
                        </div>
                        {d.created_by_name && (
                            <div className="mt-1.5 text-[10px] font-mono text-muted-foreground/80">by {d.created_by_name}</div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

export default function MyDashboardsTab({ ctx }) {
    const { viewMode, filteredDashboards, dashboards, loading } = ctx;
    if (filteredDashboards.length === 0) {
        if (dashboards.length === 0) {
            return loading ? (
                <div className="text-center py-16 text-muted-foreground text-sm" data-testid="my-dashboards-loading">
                    <Loader2 className="h-6 w-6 mx-auto mb-2 animate-spin opacity-60" />
                    Loading…
                </div>
            ) : (
                <EmptyStateCTAs context="dashboards" data-testid="my-dashboards-empty" />
            );
        }
        return (
            <div className="text-center py-16 text-muted-foreground text-sm border border-dashed border-border rounded-xl bg-secondary/20" data-testid="my-dashboards-empty">
                <LayoutDashboard className="h-10 w-10 mx-auto mb-3 opacity-30" />
                No dashboards match your filter.
            </div>
        );
    }
    const unified = filteredDashboards.filter((d) => d.is_unified);
    const single = filteredDashboards.filter((d) => !d.is_unified);
    const render = (rows) => viewMode === "details"
        ? <MyDashboardsTable rows={rows} ctx={ctx} />
        : <MyDashboardsTiles rows={rows} ctx={ctx} />;
    return (
        <>
            {unified.length > 0 && (
                <Section title="Unified dashboards" count={unified.length} testId="my-dashboards-section-unified" storageKey="unified">
                    {render(unified)}
                </Section>
            )}
            {single.length > 0 && (
                <Section title={unified.length > 0 ? "Single-source dashboards" : "Dashboards"} count={single.length} testId="my-dashboards-section-single" storageKey="single">
                    {render(single)}
                </Section>
            )}
        </>
    );
}

export { IconPlate, pickIconConfig };
